/**
 * Admin route gate for /admin and /admin-analytics.
 *
 * Loads getIdTokenResult() and allows access only when claims.admin is true.
 * Non-admins redirect to /profile. See docs/PERMISSIONS.md for the admin model.
 */
import { Button, Center, Spinner, Text, VStack } from '@chakra-ui/react';
import type { ParsedToken } from 'firebase/auth';
import { useContext, useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router';
import { Link } from 'react-router-dom';
import { VIEWPORT_MIN_H } from '@/constants/mobileLayout';
import { AuthedPageContainer } from '../components';
import { FirebaseContext } from '@/session/FirebaseContext';

const CLAIMS_TIMEOUT_MS = 10_000;

export default function AdminAuthLayout() {
  const ctx = useContext(FirebaseContext);
  const [claims, setClaims] = useState<ParsedToken>();
  const [claimsFailed, setClaimsFailed] = useState(false);

  useEffect(() => {
    if (!ctx.user) {
      setClaims(undefined);
      setClaimsFailed(false);
      return;
    }

    let cancelled = false;
    setClaims(undefined);
    setClaimsFailed(false);

    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        setClaimsFailed(true);
      }
    }, CLAIMS_TIMEOUT_MS);

    ctx.user
      .getIdTokenResult()
      .then((result) => {
        if (!cancelled) {
          setClaims(result.claims);
        }
      })
      .catch((error) => {
        console.error('[admin claims]', error);
        if (!cancelled) {
          setClaimsFailed(true);
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [ctx.user]);

  if (!ctx.authReady) {
    return (
      <Center w='full' minH={VIEWPORT_MIN_H}>
        <Spinner />
      </Center>
    );
  }

  if (claimsFailed) {
    return (
      <Center w='full' minH={VIEWPORT_MIN_H} px={4}>
        <VStack spacing={4}>
          <Text textAlign='center'>Could not verify admin access. Please try again.</Text>
          <Button as={Link} to='/profile' colorScheme='green'>
            Back to Game
          </Button>
        </VStack>
      </Center>
    );
  }

  if (claims) {
    if (claims.admin) {
      return (
        <AuthedPageContainer>
          <Outlet />
        </AuthedPageContainer>
      );
    }
    return <Navigate replace to='../profile' />;
  }

  return (
    <Center w='full' minH={VIEWPORT_MIN_H}>
      <Spinner />
    </Center>
  );
}
